import { z } from 'zod';
export const scanningMediaSemanticGeometryCapability = 'scanning-media.semantic-geometry.v1';
export const scanningMediaMeasurementQuantitiesCapability = 'scanning-media.measurement-quantities.v1';
export const scanningMediaStableKeySchema = z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, 'Scanning and Media stable keys use letters, numbers, dots, underscores, colons, and hyphens.');
export const scanningMediaSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const scanningMediaSourceUnitsSchema = z.enum(['meters', 'feet']);
export const scanningMediaSourceKindSchema = z.enum([
    'roomplan',
    'manual',
    'synthetic',
    'import',
]);
export const scanningMediaGeometryAuthoritySchema = z.enum([
    'single-session',
    'visit-frame-stitched',
    'legacy-unverified',
]);
export const scanningMediaOpeningKindSchema = z.enum(['door', 'window', 'passage']);
export const scanningMediaObjectKindSchema = z.enum([
    'fixture',
    'cabinet',
    'appliance',
    'furniture',
    'bathtub',
    'other',
]);
const sourceCoordinateValueSchema = z.number().finite().min(-10_000).max(10_000);
const sourcePositiveLengthSchema = z.number().finite().positive().max(500);
export const scanningMediaSourcePointSchema = z.strictObject({
    x: sourceCoordinateValueSchema,
    y: sourceCoordinateValueSchema,
    z: sourceCoordinateValueSchema,
});
export const scanningMediaSourceWallSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    start: scanningMediaSourcePointSchema,
    end: scanningMediaSourcePointSchema,
    height: sourcePositiveLengthSchema,
});
export const scanningMediaSourceOpeningSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    wallKey: scanningMediaStableKeySchema,
    kind: scanningMediaOpeningKindSchema,
    width: sourcePositiveLengthSchema,
    height: sourcePositiveLengthSchema,
    sillHeight: z.number().finite().min(0).max(500),
});
export const scanningMediaSourceObjectSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    kind: scanningMediaObjectKindSchema,
    label: z.string().trim().min(1).max(180).nullable().default(null),
    center: scanningMediaSourcePointSchema,
    width: sourcePositiveLengthSchema,
    height: sourcePositiveLengthSchema,
    depth: sourcePositiveLengthSchema,
});
export const scanningMediaSourceRoomSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    label: z.string().trim().min(1).max(180),
    ceilingHeight: sourcePositiveLengthSchema,
    floorPolygon: z.array(scanningMediaSourcePointSchema).min(3).max(500),
    walls: z.array(scanningMediaSourceWallSchema).min(3).max(500),
    openings: z.array(scanningMediaSourceOpeningSchema).max(500).default([]),
    objects: z.array(scanningMediaSourceObjectSchema).max(1_000).default([]),
});
export const scanningMediaSourceFloorSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    label: z.string().trim().min(1).max(180),
    elevation: z.number().finite().min(-500).max(500),
    rooms: z.array(scanningMediaSourceRoomSchema).min(1).max(250),
});
export const scanningMediaDeviceMetadataSchema = z.strictObject({
    deviceModel: z.string().trim().min(1).max(120),
    operatingSystemVersion: z.string().trim().min(1).max(80),
    applicationVersion: z.string().regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/),
    buildNumber: z.string().trim().min(1).max(80),
    deviceIdentifierHash: scanningMediaSha256Schema.nullable().default(null),
});
export const scanningMediaSourceSnapshotSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaSemanticGeometryCapability),
    sourceKind: scanningMediaSourceKindSchema,
    sourceUnits: scanningMediaSourceUnitsSchema,
    capturedAt: z.string().datetime({ offset: true }),
    coordinateFrame: z.strictObject({
        componentId: scanningMediaStableKeySchema,
        authority: scanningMediaGeometryAuthoritySchema,
        sourceSetFingerprintSha256: scanningMediaSha256Schema.nullable().default(null),
    }),
    device: scanningMediaDeviceMetadataSchema,
    floors: z.array(scanningMediaSourceFloorSchema).min(1).max(25),
});
const millimeterCoordinateSchema = z.number().int().min(-1_000_000).max(1_000_000);
const positiveMillimeterSchema = z.number().int().positive().max(500_000);
export const scanningMediaMillimeterPointSchema = z.strictObject({
    xMm: millimeterCoordinateSchema,
    yMm: millimeterCoordinateSchema,
    zMm: millimeterCoordinateSchema,
});
export const scanningMediaRoomQuantitiesSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaMeasurementQuantitiesCapability),
    floorAreaSquareCentimeters: z.number().int().positive(),
    ceilingAreaSquareCentimeters: z.number().int().positive(),
    grossWallAreaSquareCentimeters: z.number().int().positive(),
    openingAreaSquareCentimeters: z.number().int().nonnegative(),
    netWallAreaSquareCentimeters: z.number().int().nonnegative(),
    perimeterCentimeters: z.number().int().positive(),
    baseboardCentimeters: z.number().int().nonnegative(),
    volumeLiters: z.number().int().positive(),
    doorCount: z.number().int().nonnegative(),
    windowCount: z.number().int().nonnegative(),
    passageCount: z.number().int().nonnegative(),
});
export const scanningMediaSemanticWallSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    start: scanningMediaMillimeterPointSchema,
    end: scanningMediaMillimeterPointSchema,
    heightMm: positiveMillimeterSchema,
    lengthMm: positiveMillimeterSchema,
});
export const scanningMediaSemanticOpeningSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    wallKey: scanningMediaStableKeySchema,
    kind: scanningMediaOpeningKindSchema,
    widthMm: positiveMillimeterSchema,
    heightMm: positiveMillimeterSchema,
    sillHeightMm: z.number().int().nonnegative().max(500_000),
});
export const scanningMediaSemanticObjectSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    kind: scanningMediaObjectKindSchema,
    label: z.string().trim().min(1).max(180).nullable(),
    center: scanningMediaMillimeterPointSchema,
    widthMm: positiveMillimeterSchema,
    heightMm: positiveMillimeterSchema,
    depthMm: positiveMillimeterSchema,
});
export const scanningMediaSemanticRoomSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    label: z.string().trim().min(1).max(180),
    ceilingHeightMm: positiveMillimeterSchema,
    floorPolygon: z.array(scanningMediaMillimeterPointSchema).min(3).max(500),
    walls: z.array(scanningMediaSemanticWallSchema).min(3).max(500),
    openings: z.array(scanningMediaSemanticOpeningSchema).max(500),
    objects: z.array(scanningMediaSemanticObjectSchema).max(1_000),
    quantities: scanningMediaRoomQuantitiesSchema,
});
export const scanningMediaSemanticFloorSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    label: z.string().trim().min(1).max(180),
    elevationMm: millimeterCoordinateSchema,
    rooms: z.array(scanningMediaSemanticRoomSchema).min(1).max(250),
});
export const scanningMediaMeasurementTotalsSchema = scanningMediaRoomQuantitiesSchema.extend({
    roomCount: z.number().int().positive(),
    floorCount: z.number().int().positive(),
    objectCount: z.number().int().nonnegative(),
});
export const scanningMediaSemanticSnapshotSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaSemanticGeometryCapability),
    measurementContractVersion: z.literal(scanningMediaMeasurementQuantitiesCapability),
    sourceKind: scanningMediaSourceKindSchema,
    sourceUnits: scanningMediaSourceUnitsSchema,
    normalizedUnits: z.literal('millimeters'),
    capturedAt: z.string().datetime({ offset: true }),
    coordinateFrame: z.strictObject({
        componentId: scanningMediaStableKeySchema,
        authority: scanningMediaGeometryAuthoritySchema,
        sourceSetFingerprintSha256: scanningMediaSha256Schema.nullable(),
    }),
    device: scanningMediaDeviceMetadataSchema,
    floors: z.array(scanningMediaSemanticFloorSchema).min(1).max(25),
    totals: scanningMediaMeasurementTotalsSchema,
});
export class ScanningMediaGeometryValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ScanningMediaGeometryValidationError';
    }
}
function assertUniqueKeys(values, label) {
    const keys = new Set();
    for (const value of values) {
        if (keys.has(value.key)) {
            throw new ScanningMediaGeometryValidationError(`Duplicate ${label} key: ${value.key}`);
        }
        keys.add(value.key);
    }
}
function factor(units) {
    return units === 'meters' ? 1_000 : 304.8;
}
function millimeters(value, units) {
    const normalized = Math.round(value * factor(units));
    if (!Number.isSafeInteger(normalized) || Math.abs(normalized) > 1_000_000) {
        throw new ScanningMediaGeometryValidationError('Normalized coordinate exceeds the supported range.');
    }
    return normalized;
}
function positiveMillimeters(value, units) {
    const normalized = millimeters(value, units);
    if (normalized <= 0 || normalized > 500_000) {
        throw new ScanningMediaGeometryValidationError('Normalized length must be positive and bounded.');
    }
    return normalized;
}
function point(value, units) {
    return {
        xMm: millimeters(value.x, units),
        yMm: millimeters(value.y, units),
        zMm: millimeters(value.z, units),
    };
}
function horizontalLength(left, right) {
    const length = Math.round(Math.hypot(right.xMm - left.xMm, right.zMm - left.zMm));
    if (!Number.isSafeInteger(length) || length <= 0 || length > 500_000) {
        throw new ScanningMediaGeometryValidationError('Wall edges must have a positive bounded length.');
    }
    return length;
}
function polygonAreaSquareMillimeters(points) {
    let doubledArea = 0;
    for (const [index, current] of points.entries()) {
        const next = points[(index + 1) % points.length];
        if (next === undefined)
            throw new ScanningMediaGeometryValidationError('Room polygon is incomplete.');
        doubledArea += current.xMm * next.zMm - next.xMm * current.zMm;
    }
    const area = Math.round(Math.abs(doubledArea) / 2);
    if (!Number.isSafeInteger(area) || area < 10_000) {
        throw new ScanningMediaGeometryValidationError('Room polygon must enclose a measurable area.');
    }
    return area;
}
export function deriveScanningMediaRoomQuantities(input) {
    const floorAreaSquareMillimeters = polygonAreaSquareMillimeters(input.floorPolygon);
    const perimeterMillimeters = input.walls.reduce((sum, wall) => sum + wall.lengthMm, 0);
    const grossWallAreaSquareMillimeters = input.walls.reduce((sum, wall) => sum + wall.lengthMm * wall.heightMm, 0);
    const openingAreaSquareMillimeters = input.openings.reduce((sum, opening) => sum + opening.widthMm * opening.heightMm, 0);
    const nonBaseboardWidthMillimeters = input.openings
        .filter(({ kind }) => kind === 'door' || kind === 'passage')
        .reduce((sum, opening) => sum + opening.widthMm, 0);
    const doorCount = input.openings.filter(({ kind }) => kind === 'door').length;
    const windowCount = input.openings.filter(({ kind }) => kind === 'window').length;
    const passageCount = input.openings.filter(({ kind }) => kind === 'passage').length;
    return scanningMediaRoomQuantitiesSchema.parse({
        contractVersion: scanningMediaMeasurementQuantitiesCapability,
        floorAreaSquareCentimeters: Math.round(floorAreaSquareMillimeters / 100),
        ceilingAreaSquareCentimeters: Math.round(floorAreaSquareMillimeters / 100),
        grossWallAreaSquareCentimeters: Math.round(grossWallAreaSquareMillimeters / 100),
        openingAreaSquareCentimeters: Math.round(openingAreaSquareMillimeters / 100),
        netWallAreaSquareCentimeters: Math.max(0, Math.round((grossWallAreaSquareMillimeters - openingAreaSquareMillimeters) / 100)),
        perimeterCentimeters: Math.round(perimeterMillimeters / 10),
        baseboardCentimeters: Math.max(0, Math.round((perimeterMillimeters - nonBaseboardWidthMillimeters) / 10)),
        volumeLiters: Math.round(floorAreaSquareMillimeters * input.ceilingHeightMm / 1_000_000),
        doorCount,
        windowCount,
        passageCount,
    });
}
export function deriveScanningMediaMeasurementTotals(floors) {
    const roomList = floors.flatMap(({ rooms }) => rooms);
    return scanningMediaMeasurementTotalsSchema.parse(roomList.reduce((result, room) => ({
        contractVersion: scanningMediaMeasurementQuantitiesCapability,
        floorAreaSquareCentimeters: result.floorAreaSquareCentimeters + room.quantities.floorAreaSquareCentimeters,
        ceilingAreaSquareCentimeters: result.ceilingAreaSquareCentimeters + room.quantities.ceilingAreaSquareCentimeters,
        grossWallAreaSquareCentimeters: result.grossWallAreaSquareCentimeters + room.quantities.grossWallAreaSquareCentimeters,
        openingAreaSquareCentimeters: result.openingAreaSquareCentimeters + room.quantities.openingAreaSquareCentimeters,
        netWallAreaSquareCentimeters: result.netWallAreaSquareCentimeters + room.quantities.netWallAreaSquareCentimeters,
        perimeterCentimeters: result.perimeterCentimeters + room.quantities.perimeterCentimeters,
        baseboardCentimeters: result.baseboardCentimeters + room.quantities.baseboardCentimeters,
        volumeLiters: result.volumeLiters + room.quantities.volumeLiters,
        doorCount: result.doorCount + room.quantities.doorCount,
        windowCount: result.windowCount + room.quantities.windowCount,
        passageCount: result.passageCount + room.quantities.passageCount,
        roomCount: result.roomCount + 1,
        floorCount: floors.length,
        objectCount: result.objectCount + room.objects.length,
    }), {
        contractVersion: scanningMediaMeasurementQuantitiesCapability,
        floorAreaSquareCentimeters: 0,
        ceilingAreaSquareCentimeters: 0,
        grossWallAreaSquareCentimeters: 0,
        openingAreaSquareCentimeters: 0,
        netWallAreaSquareCentimeters: 0,
        perimeterCentimeters: 0,
        baseboardCentimeters: 0,
        volumeLiters: 0,
        doorCount: 0,
        windowCount: 0,
        passageCount: 0,
        roomCount: 0,
        floorCount: floors.length,
        objectCount: 0,
    }));
}
/**
 * Converts provider/device floating measurements into the canonical integer-
 * millimeter geometry and integer quantity contract. Arrays keep capture order;
 * object hashing happens later over the validated output.
 */
export function normalizeScanningMediaSemanticSnapshot(input) {
    const source = scanningMediaSourceSnapshotSchema.parse(input);
    assertUniqueKeys(source.floors, 'floor');
    const roomKeys = new Set();
    const floors = source.floors.map((sourceFloor) => {
        assertUniqueKeys(sourceFloor.rooms, `room in floor ${sourceFloor.key}`);
        const rooms = sourceFloor.rooms.map((sourceRoom) => {
            if (roomKeys.has(sourceRoom.key)) {
                throw new ScanningMediaGeometryValidationError(`Duplicate room key: ${sourceRoom.key}`);
            }
            roomKeys.add(sourceRoom.key);
            assertUniqueKeys(sourceRoom.walls, `wall in room ${sourceRoom.key}`);
            assertUniqueKeys(sourceRoom.openings, `opening in room ${sourceRoom.key}`);
            assertUniqueKeys(sourceRoom.objects, `object in room ${sourceRoom.key}`);
            const floorPolygon = sourceRoom.floorPolygon.map((value) => point(value, source.sourceUnits));
            const walls = sourceRoom.walls.map((wall) => {
                const start = point(wall.start, source.sourceUnits);
                const end = point(wall.end, source.sourceUnits);
                return {
                    key: wall.key,
                    start,
                    end,
                    heightMm: positiveMillimeters(wall.height, source.sourceUnits),
                    lengthMm: horizontalLength(start, end),
                };
            });
            const wallKeys = new Set(walls.map(({ key }) => key));
            const wallsByKey = new Map(walls.map((wall) => [wall.key, wall]));
            const openings = sourceRoom.openings.map((opening) => {
                if (!wallKeys.has(opening.wallKey)) {
                    throw new ScanningMediaGeometryValidationError(`Opening ${opening.key} references missing wall ${opening.wallKey}.`);
                }
                const normalized = {
                    key: opening.key,
                    wallKey: opening.wallKey,
                    kind: opening.kind,
                    widthMm: positiveMillimeters(opening.width, source.sourceUnits),
                    heightMm: positiveMillimeters(opening.height, source.sourceUnits),
                    sillHeightMm: millimeters(opening.sillHeight, source.sourceUnits),
                };
                const ownerWall = wallsByKey.get(opening.wallKey);
                if (ownerWall === undefined ||
                    normalized.widthMm > ownerWall.lengthMm ||
                    normalized.sillHeightMm + normalized.heightMm > ownerWall.heightMm) {
                    throw new ScanningMediaGeometryValidationError(`Opening ${opening.key} exceeds wall ${opening.wallKey}.`);
                }
                return normalized;
            });
            const objects = sourceRoom.objects.map((object) => ({
                key: object.key,
                kind: object.kind,
                label: object.label,
                center: point(object.center, source.sourceUnits),
                widthMm: positiveMillimeters(object.width, source.sourceUnits),
                heightMm: positiveMillimeters(object.height, source.sourceUnits),
                depthMm: positiveMillimeters(object.depth, source.sourceUnits),
            }));
            const ceilingHeightMm = positiveMillimeters(sourceRoom.ceilingHeight, source.sourceUnits);
            return {
                key: sourceRoom.key,
                label: sourceRoom.label,
                ceilingHeightMm,
                floorPolygon,
                walls,
                openings,
                objects,
                quantities: deriveScanningMediaRoomQuantities({
                    floorPolygon,
                    walls,
                    openings,
                    ceilingHeightMm,
                }),
            };
        });
        return {
            key: sourceFloor.key,
            label: sourceFloor.label,
            elevationMm: millimeters(sourceFloor.elevation, source.sourceUnits),
            rooms,
        };
    });
    const totals = deriveScanningMediaMeasurementTotals(floors);
    return scanningMediaSemanticSnapshotSchema.parse({
        contractVersion: scanningMediaSemanticGeometryCapability,
        measurementContractVersion: scanningMediaMeasurementQuantitiesCapability,
        sourceKind: source.sourceKind,
        sourceUnits: source.sourceUnits,
        normalizedUnits: 'millimeters',
        capturedAt: source.capturedAt,
        coordinateFrame: source.coordinateFrame,
        device: source.device,
        floors,
        totals,
    });
}
//# sourceMappingURL=scanning-media-semantic-geometry.contract.js.map